import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { CoreService } from 'src/app/services/core/core.service';
import { HrDataService } from '../../../hr.service';

/**
 * A file uploaded via the existing generic attachments endpoint (same one
 * Product's "Add Picture" uses) as soon as it's added to the dialog. Only its
 * attachment_id is sent when the request is submitted (as file_ids); there is
 * no time_off_request_id to link it to until then.
 */
export interface StagedTimeOffDocument {
  file: File;
  preview: string | null;
  attachmentId: number | null;
  attachFile: any;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

/**
 * Dialog data interface for PTO request
 */
export interface PTORequestDialogData {
  staffId: number;
  staffName: string;
  ptoBalance: {
    vacation_days: number;
    vacation_total: number;
    sick_days: number;
    sick_total: number;
    personal_days: number;
    personal_total: number;
  };
}

/**
 * Dialog result interface for PTO request
 */
export interface PTORequestDialogResult {
  type: 'Vacation' | 'Sick' | 'Personal';
  start_date: string;
  end_date: string;
  days: number;
  notes?: string;
  file_ids?: number[];
}

/**
 * PTO type option for dropdown
 */
interface PTOTypeOption {
  value: 'Vacation' | 'Sick' | 'Personal';
  label: string;
  icon: string;
  available: number;
  total: number;
  color: string;
}

@Component({
    selector: 'app-pto-request-dialog',
    templateUrl: './pto-request-dialog.component.html',
    styleUrls: ['./pto-request-dialog.component.scss'],
    standalone: false
})
export class PTORequestDialogComponent implements OnInit {
  public requestForm: UntypedFormGroup = this.initForm();
  public isSubmitting: boolean = false;
  public calculatedDays: number = 0;
  public minDate: Date = new Date();
  public selectedType: PTOTypeOption | null = null;

  // PTO type options for dropdown
  public ptoTypes: PTOTypeOption[] = [];

  // ============================================================================
  // DOCUMENTS - uploaded immediately via the existing generic attachments
  // endpoint as they're added; only attachment ids are sent on submit (as
  // file_ids), since there is no time_off_request_id until the request exists.
  // ============================================================================
  public stagedDocuments: StagedTimeOffDocument[] = [];
  public isDraggingOverDialog: boolean = false;
  public draggedFileCount: number = 0;
  public readonly allowedDocumentTypes: string[] = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.doc', '.docx', '.xls', '.xlsx'];
  public readonly maxDocumentSizeBytes: number = 10 * 1024 * 1024; // 10MB, matches backend max:10240

  constructor(
    public toasterService: ToasterService,
    public coreService: CoreService,
    public hrDataService: HrDataService,
    @Inject(MAT_DIALOG_DATA) public data: PTORequestDialogData,
    public dialogRef: MatDialogRef<PTORequestDialogComponent>
  ) {
    this.initPTOTypes();
  }

  ngOnInit(): void {
    this.requestForm = this.initForm();
    // Set min date to today
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);
  }

  /**
   * Initialize PTO type options from balance data
   */
  initPTOTypes(): void {
    this.ptoTypes = [
      {
        value: 'Vacation',
        label: 'Vacation',
        icon: 'wb_sunny',
        available: this.data.ptoBalance.vacation_days,
        total: this.data.ptoBalance.vacation_total,
        color: '#42a5f5'
      },
      {
        value: 'Sick',
        label: 'Sick Leave',
        icon: 'local_hospital',
        available: this.data.ptoBalance.sick_days,
        total: this.data.ptoBalance.sick_total,
        color: '#ef5350'
      },
      {
        value: 'Personal',
        label: 'Personal',
        icon: 'person',
        available: this.data.ptoBalance.personal_days,
        total: this.data.ptoBalance.personal_total,
        color: '#9c27b0'
      }
    ];
  }

  /**
   * Initialize the form with validators
   */
  initForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      request_type: new UntypedFormControl(null, [Validators.required]),
      start_date: new UntypedFormControl(null, [Validators.required]),
      end_date: new UntypedFormControl(null, [Validators.required]),
      notes: new UntypedFormControl('', [Validators.maxLength(500)])
    });
  }

  /**
   * Handle PTO type selection change
   */
  onTypeChange(typeValue: string): void {
    this.selectedType = this.ptoTypes.find(t => t.value === typeValue) || null;
    this.validateDaysAgainstBalance();
  }

  /**
   * Handle date change - recalculate days
   */
  onDateChange(): void {
    const startDate = this.requestForm.get('start_date')?.value;
    const endDate = this.requestForm.get('end_date')?.value;

    if (startDate && endDate) {
      this.calculatedDays = this.calculateBusinessDays(new Date(startDate), new Date(endDate));
      this.validateDaysAgainstBalance();
    } else {
      this.calculatedDays = 0;
    }
  }

  /**
   * Format a Date as a timezone-safe local YYYY-MM-DD string.
   * Do not use toISOString().split('T')[0] here: toISOString() converts
   * to UTC first, which shifts the calendar date back a day for any user
   * in a timezone ahead of UTC (e.g. UTC+5) while leaving the picked date
   * (year/month/day in local time) untouched.
   */
  toLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Calculate business days between two dates (excluding weekends)
   */
  calculateBusinessDays(startDate: Date, endDate: Date): number {
    if (endDate < startDate) return 0;

    let count = 0;
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      // Exclude weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return count;
  }

  /**
   * Validate requested days against available balance
   */
  validateDaysAgainstBalance(): boolean {
    if (!this.selectedType || this.calculatedDays === 0) return true;
    return this.calculatedDays <= this.selectedType.available;
  }

  /**
   * Get error message for days validation
   */
  getDaysValidationError(): string {
    if (!this.selectedType) return '';
    if (this.calculatedDays > this.selectedType.available) {
      return `Exceeds available balance (${this.selectedType.available} days)`;
    }
    return '';
  }

  /**
   * Get remaining balance after request approval
   */
  getRemainingBalance(): number {
    if (!this.selectedType) return 0;
    return Math.max(0, this.selectedType.available - this.calculatedDays);
  }

  /**
   * Get usage percentage for progress bar
   */
  getUsagePercentage(): number {
    if (!this.selectedType || this.selectedType.total === 0) return 0;
    const used = this.selectedType.total - this.selectedType.available;
    return Math.round((used / this.selectedType.total) * 100);
  }

  /**
   * Check if form is valid for submission
   */
  isFormValid(): boolean {
    if (!this.requestForm.valid) return false;
    if (this.calculatedDays === 0) return false;
    if (!this.validateDaysAgainstBalance()) return false;

    // Validate end date >= start date
    const startDate = this.requestForm.get('start_date')?.value;
    const endDate = this.requestForm.get('end_date')?.value;
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      return false;
    }

    // Don't allow submitting while a document is still uploading
    if (this.stagedDocuments.some(d => d.status === 'uploading')) {
      return false;
    }

    return true;
  }

  // ============================================================================
  // DOCUMENTS - drag & drop (whole-dialog overlay) + contained box at rest
  // ============================================================================

  /** Bound on the dialog root so dragging anywhere over it shows the overlay. */
  onDialogDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDialog = true;
    const items = event.dataTransfer?.items;
    this.draggedFileCount = items ? Array.from(items).filter(i => i.kind === 'file').length : 0;
  }

  onDialogDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDialogDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Only clear once the pointer actually leaves the dialog, not when it
    // moves between child elements (which also fire dragleave).
    const related = event.relatedTarget as Node | null;
    const currentTarget = event.currentTarget as Node;
    if (related && currentTarget.contains(related)) {
      return;
    }
    this.isDraggingOverDialog = false;
    this.draggedFileCount = 0;
  }

  onDialogDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDialog = false;
    this.draggedFileCount = 0;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addDocuments(files);
    }
  }

  onDocumentInputChange(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.addDocuments(files);
    }
    event.target.value = '';
  }

  addDocuments(fileList: FileList): void {
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      if (!this.isValidDocumentType(file)) {
        this.toasterService.showWarning(`${file.name} is not an allowed file type`);
        continue;
      }
      if (!this.isValidDocumentSize(file)) {
        this.toasterService.showWarning(`${file.name} is too large (max 10MB)`);
        continue;
      }

      const staged: StagedTimeOffDocument = {
        file,
        preview: null,
        attachmentId: null,
        attachFile: null,
        status: 'uploading',
        progress: 0
      };
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => (staged.preview = e.target.result);
        reader.readAsDataURL(file);
      }
      this.stagedDocuments.push(staged);
      this.uploadStagedDocument(staged);
    }
  }

  /** Uploads through the existing generic attachments endpoint, same as Product's "Add Picture". */
  private uploadStagedDocument(staged: StagedTimeOffDocument): void {
    this.hrDataService.uploadAttachmentWithProgress(staged.file).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total) {
            staged.progress = Math.round((100 * event.loaded) / event.total);
          }
        } else if (event.type === HttpEventType.Response) {
          const response = event.body;
          staged.progress = 100;
          staged.status = 'success';
          staged.attachmentId = response?.data?.attachment_id;
          staged.attachFile = response?.data?.attach_file;
        }
      },
      error: () => {
        staged.status = 'error';
        staged.error = 'Upload failed';
        this.toasterService.showError('Upload Failed', `Could not upload "${staged.file.name}"`);
      }
    });
  }

  removeStagedDocument(index: number): void {
    const staged = this.stagedDocuments[index];
    this.stagedDocuments.splice(index, 1);

    // Already uploaded to attach_files but not yet linked to a request (that
    // only happens on submit) — delete it now so it doesn't stay orphaned.
    if (staged.status === 'success' && staged.attachmentId) {
      this.hrDataService.deleteAttachment(staged.attachmentId).subscribe();
    }
  }

  private isValidDocumentType(file: File): boolean {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return this.allowedDocumentTypes.includes(ext);
  }

  private isValidDocumentSize(file: File): boolean {
    return file.size <= this.maxDocumentSizeBytes;
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getDocumentIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return 'image';
    if (ext === 'pdf') return 'picture_as_pdf';
    if (['doc', 'docx'].includes(ext || '')) return 'description';
    if (['xls', 'xlsx'].includes(ext || '')) return 'table_chart';
    return 'insert_drive_file';
  }

  /** File-type accent (red PDF, blue Word, green Excel) instead of one flat grey icon for every file. */
  getDocumentTypeClass(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext || '')) return 'doc';
    if (['xls', 'xlsx'].includes(ext || '')) return 'xls';
    return 'generic';
  }

  get acceptedDocumentTypes(): string {
    return this.allowedDocumentTypes.join(',');
  }

  /**
   * Submit the PTO request
   */
  onSubmit(): void {
    if (!this.isFormValid()) {
      this.toasterService.showError('Validation Error', 'Please fill all required fields correctly');
      return;
    }

    this.isSubmitting = true;

    const formValue = this.requestForm.getRawValue();

    // Format dates properly to YYYY-MM-DD
    let startDateFormatted = '';
    let endDateFormatted = '';

    try {
      // Convert date to YYYY-MM-DD format
      const startDate = new Date(formValue.start_date);
      const endDate = new Date(formValue.end_date);

      startDateFormatted = this.toLocalDateString(startDate);
      endDateFormatted = this.toLocalDateString(endDate);
    } catch (error) {
      this.toasterService.showError('Date Error', 'Invalid date selection');
      this.isSubmitting = false;
      return;
    }

    // Ensure dates are valid before proceeding
    if (!startDateFormatted || !endDateFormatted) {
      this.toasterService.showError('Date Error', 'Invalid date selection');
      this.isSubmitting = false;
      return;
    }

    const fileIds = this.stagedDocuments
      .filter(d => d.status === 'success' && d.attachmentId)
      .map(d => d.attachmentId as number);

    const resultData: PTORequestDialogResult = {
      type: formValue.request_type,
      start_date: startDateFormatted,
      end_date: endDateFormatted,
      days: this.calculatedDays,
      notes: formValue.notes || undefined,
      file_ids: fileIds.length > 0 ? fileIds : undefined
    };

    // Simulate API delay for better UX
    setTimeout(() => {
      this.close(resultData);
    }, 300);
  }

  /**
   * Close dialog
   */
  close(data: PTORequestDialogResult | null = null): void {
    this.dialogRef.close(data);
  }

  /**
   * Get min end date (must be >= start date)
   */
  getMinEndDate(): Date {
    const startDate = this.requestForm.get('start_date')?.value;
    if (startDate) {
      return new Date(startDate);
    }
    return this.minDate;
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Check if request summary should be shown
   */
  showRequestSummary(): boolean {
    return this.selectedType !== null &&
           this.calculatedDays > 0 &&
           this.requestForm.get('start_date')?.value &&
           this.requestForm.get('end_date')?.value;
  }
}
